// @ts-nocheck
/**
 * VerticalSidebar Component
 *
 * Desktop-only vertical icon sidebar with primary navigation tabs.
 * Features:
 * - Collapsible: 56px wide (icon only) or 96px wide (icon + text labels)
 * - Active state highlighting with border and background
 * - Tooltips on hover (when collapsed)
 * - Keyboard accessible with ARIA labels
 * - Expand/collapse toggle persists across sessions
 * - Phase-based filtering: Shows only tabs for the current workflow phase
 *
 * Design: Mobile-first (hidden on mobile, visible on desktop md:flex)
 */

import React, { cloneElement, isValidElement, useMemo, useCallback } from 'react';
import { Layout, ChevronLeft, ChevronRight, Code2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppTab } from '../../../../types';
import { useUIStore } from '@shared/store/uiStore';
import { AppLogo } from '@shared/components/branding/AppLogo';
import { useAdminEditorStore } from '@features/admin/store/adminEditorStore';
import { TAB_TO_ROUTE, ROUTES } from '@app/routes';

export interface VerticalSidebarProps {
  /** Currently active tab */
  activeTab: AppTab;
  /** Callback when a tab is clicked */
  onTabClick: (tab: AppTab) => void;
  /** Map of tab names to their icon components */
  tabIcons: Record<AppTab, React.ReactNode>;
  /** List of all available tabs in order */
  tabs: AppTab[];
}

/**
 * Get short label for tab (first word or abbreviation)
 */
const getShortLabel = (tab: string): string => {
  const labelMap: Record<string, string> = {
    'Campaign Management': 'Campaigns',
    'Organization Management': 'Orgs',
    'Address Management': 'Address',
    'Route Planning': 'Routes',
    'Review Routes': 'Review',
    'Contact Management': 'Contacts',
    'Label Generator': 'Labels',
    'Execution Tracking': 'Tracking',
    'Handwrytten Export': 'Export',
    'Event Planning Grid': 'Events',
    'Reports': 'Reports',
    'Report Generator': 'OMNI',
  };
  return labelMap[tab] || tab.split(' ')[0];
};

export const VerticalSidebar: React.FC<VerticalSidebarProps> = ({
  activeTab,
  onTabClick,
  tabIcons,
  tabs,
}) => {
  const navigate = useNavigate();
  const { sidebarExpanded, toggleSidebarExpanded, focusMode } = useUIStore();
  const { currentPhase, phases } = useAdminEditorStore();

  const handleDevDocsClick = useCallback(() => {
    navigate(ROUTES.APP_HOME);
  }, [navigate]);

  /**
   * Filter tabs based on current phase.
   * If no phase is selected (null), show all tabs.
   * If a phase is selected, show only tabs that are visible in that phase.
   */
  const filteredTabs = useMemo(() => {
    // If no phase is selected, show all tabs
    if (!currentPhase) {
      return tabs;
    }

    // Find the current phase configuration
    const currentPhaseConfig = phases.find((p) => p.type === currentPhase);

    // If phase config not found or has no pages, show all tabs as fallback
    if (!currentPhaseConfig || currentPhaseConfig.pages.length === 0) {
      return tabs;
    }

    // Get visible pages for the current phase
    const visiblePages = currentPhaseConfig.pages.filter((page) => page.visible);

    // Create a set of routes that are visible in this phase for fast lookup
    const visibleRoutes = new Set(visiblePages.map((page) => page.route));

    // Filter tabs to only those whose routes are in the visible set
    const filtered = tabs.filter((tab) => {
      const tabRoute = TAB_TO_ROUTE[tab];
      if (!tabRoute) return false;

      // Check if the tab's route matches any visible page route
      // Handle both exact matches and prefix matches (e.g., /app/dashboard matches /app/dashboard)
      return visiblePages.some((page) => {
        // Exact match
        if (page.route === tabRoute) return true;
        // Route prefix match (for nested routes)
        if (tabRoute.startsWith(page.route + '/') || page.route.startsWith(tabRoute + '/')) return true;
        // Base route match (e.g., /app/reports/drop-report should match 'Reports' tab)
        if (page.route.startsWith(tabRoute)) return true;
        return false;
      });
    });

    // If filtering results in no tabs, fall back to showing all tabs
    return filtered.length > 0 ? filtered : tabs;
  }, [currentPhase, phases, tabs]);

  // Hide completely when focus mode is active
  if (focusMode) {
    return null;
  }

  return (
    <div
      className={`
        hidden md:flex flex-col bg-gradient-to-b from-dark-surface via-dark-surface/95 to-dark-bg/90 border-r border-dark-border/30 shrink-0 transition-all duration-200 ease-out
        ${sidebarExpanded ? 'w-24' : 'w-14'}
      `}
    >
      {/* App Logo - top left corner */}
      <div className="flex items-center justify-center h-10 mt-1.5 shrink-0">
        <AppLogo size={sidebarExpanded ? 32 : 28} animate />
      </div>

      {/* Tab Icons - pt-4 for spacing below logo */}
      <div className={`flex-1 flex flex-col items-center pt-4 pb-2 overflow-y-auto overflow-x-hidden ${sidebarExpanded ? 'space-y-2' : 'space-y-1'}`}>
        {filteredTabs.map(tab => {
          const isActive = activeTab === tab;
          const icon = tabIcons[tab] || <Layout size={sidebarExpanded ? 18 : 14} />;

          // Clone icon with updated size when expanded
          const sizedIcon = isValidElement(icon)
            ? cloneElement(icon as React.ReactElement<{ size?: number }>, {
                size: sidebarExpanded ? 18 : 14,
              })
            : icon;

          return (
            <button
              key={tab}
              onClick={() => onTabClick(tab)}
              className={`
                flex flex-col items-center justify-center rounded-xl transition-all duration-200 ease-out relative group
                ${sidebarExpanded ? 'w-20 h-14 py-1.5 gap-0.5' : 'w-10 h-10'}
                ${isActive
                  ? 'bg-gradient-to-br from-brand-500/20 to-brand-600/10 text-brand-400 border border-brand-500/25 shadow-sm shadow-brand-500/10'
                  : 'text-dark-muted hover:bg-dark-surface/60 hover:text-white border border-transparent hover:border-dark-border/20'}
              `}
              title={tab}
              aria-label={tab}
              aria-current={isActive ? 'page' : undefined}
              data-testid={`sidebar-tab-${tab.toLowerCase().replace(/\s+/g, '-')}`}
              data-ux='action'
            >
              {sizedIcon}
              {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-6 bg-brand-500 rounded-r" />}

              {/* Text label when expanded */}
              {sidebarExpanded && (
                <span className="text-[9px] leading-tight text-center px-0.5 max-w-full truncate">
                  {getShortLabel(tab)}
                </span>
              )}

              {/* Tooltip on hover (only when collapsed) */}
              {!sidebarExpanded && (
                <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-dark-surface/95 backdrop-blur-md border border-dark-border/30 rounded-xl text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none z-50 shadow-lg shadow-black/20">
                  {tab}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Dev Docs Link - at bottom above toggle */}
      <div className="px-2 pb-1">
        <button
          onClick={handleDevDocsClick}
          className={`
            flex flex-col items-center justify-center rounded-lg transition-all duration-200 w-full group relative
            ${sidebarExpanded ? 'py-1.5 gap-0.5' : 'h-8'}
            text-dark-muted hover:bg-dark-surface/60 hover:text-white
          `}
          title="Dev Docs"
          aria-label="Dev Docs - Tech stack and roadmap"
          data-testid="sidebar-dev-docs"
          data-ux='action'
        >
          <Code2 size={sidebarExpanded ? 16 : 14} />
          {sidebarExpanded && (
            <span className="text-[9px] leading-tight">Dev Docs</span>
          )}
          {!sidebarExpanded && (
            <div className="absolute left-full ml-2 px-2.5 py-1.5 bg-dark-surface/95 backdrop-blur-md border border-dark-border/30 rounded-xl text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-150 pointer-events-none z-50 shadow-lg shadow-black/20">
              Dev Docs
            </div>
          )}
        </button>
      </div>

      {/* Expand/Collapse Toggle Button */}
      <div className="border-t border-dark-border/20 p-2 flex justify-center">
        <button
          onClick={toggleSidebarExpanded}
          className="flex items-center justify-center w-8 h-8 rounded-lg text-dark-muted hover:bg-dark-surface/60 hover:text-white transition-all duration-200"
          title={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          aria-label={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          data-testid="sidebar-toggle"
          data-ux='action'
        >
          {sidebarExpanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
      </div>
    </div>
  );
};

